import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFieldArray, useForm } from 'react-hook-form';
import { SimpleMode } from '@/components/jobs/SimpleMode';
import { TemplateMode } from '@/components/jobs/TemplateMode';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, HelpCircle, Play, Plus, Save, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select, Textarea, Toggle } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/cn';

const fieldSchema = z.object({
  name: z.string().trim().min(1, 'Nama field wajib diisi.').max(60),
  selector: z.string().trim().min(1, 'Selector wajib diisi.').max(500),
  type: z.enum(['text', 'number', 'link', 'image', 'html', 'date', 'attribute']),
  attribute: z.string().trim().max(60).optional().or(z.literal('')),
});

const schema = z
  .object({
    name: z.string().trim().min(1, 'Nama job wajib diisi.').max(120),
    description: z.string().trim().max(500).optional().or(z.literal('')),
    targetUrl: z
      .string()
      .trim()
      .min(1, 'URL sumber wajib diisi.')
      .url('URL tidak valid. Sertakan http:// atau https://.'),
    engine: z.enum(['auto', 'static', 'dynamic', 'playwright']),
    itemSelector: z.string().trim().max(500).optional().or(z.literal('')),
    paginationSelector: z.string().trim().max(500).optional().or(z.literal('')),
    depth: z.coerce.number().int().min(0).max(5),
    maxPages: z.coerce.number().int().min(1).max(10_000),
    delayMs: z.coerce.number().int().min(200).max(60_000),
    respectRobotsTxt: z.boolean(),
    scheduleType: z.enum(['once', 'hourly', 'daily', 'weekly', 'monthly']),
    scheduleEnabled: z.boolean(),
    timeOfDay: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Format jam harus HH:mm.'),
    outputFormat: z.enum(['table', 'json', 'csv']),
    deduplicate: z.boolean(),
    fields: z.array(fieldSchema).min(1, 'Tambahkan minimal satu field.').max(50),
  })
  .refine(
    (data) => new Set(data.fields.map((f) => f.name.toLowerCase())).size === data.fields.length,
    { message: 'Nama field tidak boleh sama.', path: ['fields'] },
  );

const FIELD_TYPES = [
  { value: 'text', label: 'Teks' },
  { value: 'number', label: 'Angka' },
  { value: 'link', label: 'Tautan' },
  { value: 'image', label: 'Gambar' },
  { value: 'date', label: 'Tanggal' },
  { value: 'html', label: 'HTML mentah' },
  { value: 'attribute', label: 'Atribut' },
];

/**
 * Layar Buat Scraping.
 *
 * Perbedaan utama dari rancangan: setiap kolom konfigurasi kini punya teks
 * bantuan, ada panduan selector yang bisa dibuka, dan panel ringkasan di kanan
 * memperbarui dirinya sesuai isian - bukan lagi angka statis.
 */
const SCHEDULE_LABELS = {
  once: 'Sekali jalan',
  hourly: 'Setiap jam',
  daily: 'Harian',
  weekly: 'Mingguan',
  monthly: 'Bulanan',
};

const OUTPUT_LABELS = {
  table: 'Tabel terstruktur',
  json: 'JSON',
  csv: 'CSV',
};

const ManualMode = () => {
  const navigate = useNavigate();
  const toast = useToast();

  // Batas paket dibaca dari potret pemakaian yang sama dengan halaman
  // Penagihan, bukan dari daftar paket: yang berlaku untuk ruang kerja ini
  // sudah memperhitungkan penimpaan admin dan add-on.
  const { data: pemakaian } = useQuery({
    queryKey: ['usage'],
    queryFn: () => api.get('/dashboard/usage'),
  });
  const bolehAturRobots = Boolean(pemakaian?.data?.features?.robotsOpsional);
  const namaPaket = pemakaian?.data?.plan?.name ?? 'ini';
  const [helpOpen, setHelpOpen] = useState(false);

  const form = useForm({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      description: '',
      targetUrl: '',
      engine: 'auto',
      itemSelector: '',
      paginationSelector: '',
      depth: 0,
      maxPages: 50,
      delayMs: 1000,
      respectRobotsTxt: true,
      scheduleType: 'once',
      scheduleEnabled: false,
      timeOfDay: '09:00',
      outputFormat: 'table',
      deduplicate: true,
      fields: [{ name: '', selector: '', type: 'text', attribute: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'fields',
  });
  const values = form.watch();

  const mutation = useMutation({
    mutationFn: ({ payload, runNow }) => api.post('/jobs', { ...payload, runNow }),
    onSuccess: (response, variables) => {
      const job = response.data;
      toast.success(variables.runNow ? 'Crawl dimulai' : 'Job disimpan sebagai draf', {
        description: variables.runNow
          ? 'Anda bisa memantau progresnya secara langsung.'
          : 'Jalankan kapan saja dari halaman detail.',
      });
      navigate(`/jobs/${job.id}`);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        const fieldErrors = error.fieldErrors;
        for (const [key, message] of Object.entries(fieldErrors)) {
          // Galat dari server memakai jalur seperti "fields.0.selector".
          form.setError(key.replace(/^body\./, ''), { message });
        }
        toast.error('Job tidak bisa disimpan', { description: error.message });
      }
    },
  });

  const submit = (runNow) =>
    form.handleSubmit((data) => {
      const payload = {
        name: data.name,
        description: data.description || '',
        targetUrl: data.targetUrl,
        engine: data.engine,
        outputFormat: data.outputFormat,
        deduplicate: data.deduplicate,
        crawl: {
          depth: data.depth,
          maxPages: data.maxPages,
          delayMs: data.delayMs,
          respectRobotsTxt: data.respectRobotsTxt,
          itemSelector: data.itemSelector || null,
          paginationSelector: data.paginationSelector || null,
        },
        fields: data.fields.map((f) => ({
          name: f.name,
          selector: f.selector,
          type: f.type,
          attribute: f.type === 'attribute' ? f.attribute || null : null,
        })),
        schedule: {
          type: data.scheduleType,
          enabled: data.scheduleEnabled && data.scheduleType !== 'once',
          timeOfDay: data.timeOfDay,
        },
      };
      mutation.mutate({ payload, runNow });
    });

  return (
    <form className="grid gap-4 lg:grid-cols-3" noValidate>
      <div className="space-y-4 lg:col-span-2">
        {/* ── Detail dasar ────────────────────────────────────────────── */}
        <Card>
          <CardHeader title="Detail Scraping" />
          <CardBody className="space-y-4">
            <Field label="Nama Job" required error={form.formState.errors.name?.message}>
              {(props) => (
                <Input
                  {...props}
                  {...form.register('name')}
                  placeholder="Misal: Harga Kompetitor Mingguan"
                  error={form.formState.errors.name}
                />
              )}
            </Field>

            <Field
              label="URL Sumber"
              required
              error={form.formState.errors.targetUrl?.message}
              hint="Mode Manual menuntut Anda mengisi selektornya sendiri. Belum terbiasa? Buka panduan di bawah, atau pakai tab Otomatis."
            >
              {(props) => (
                <Input
                  {...props}
                  {...form.register('targetUrl')}
                  type="url"
                  placeholder="https://contoh.com/produk"
                  error={form.formState.errors.targetUrl}
                />
              )}
            </Field>

            <Field
              label="Deskripsi"
              hint="Opsional - membantu rekan tim memahami tujuan job ini."
            >
              {(props) => (
                <Textarea
                  {...props}
                  {...form.register('description')}
                  placeholder="Untuk apa data ini dipakai?"
                  rows={2}
                />
              )}
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Mesin Crawl"
                hint="Otomatis memilih sendiri: cepat dulu, browser bila perlu."
              >
                {(props) => (
                  <Select
                    {...props}
                    {...form.register('engine')}
                    options={[
                      { value: 'auto', label: 'Otomatis (disarankan)' },
                      { value: 'static', label: 'Cepat - HTML statis' },
                      { value: 'dynamic', label: 'Browser Chrome - halaman JavaScript' },
                      { value: 'playwright', label: 'Browser Playwright - kalau Chrome gagal' },
                    ]}
                  />
                )}
              </Field>

              <Field label="Format Output">
                {(props) => (
                  <Select
                    {...props}
                    {...form.register('outputFormat')}
                    options={[
                      { value: 'table', label: 'Tabel terstruktur' },
                      { value: 'json', label: 'JSON' },
                      { value: 'csv', label: 'CSV' },
                    ]}
                  />
                )}
              </Field>
            </div>
          </CardBody>
        </Card>

        {/* ── Cakupan crawl ───────────────────────────────────────────── */}
        <Card>
          <CardHeader
            title="Cakupan Crawl"
            description="Sejauh mana Crawel menelusuri situsnya, dan seberapa sering ia meminta halaman baru."
          />
          <CardBody className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field
                label="Kedalaman"
                hint="0 = hanya halaman ini."
                error={form.formState.errors.depth?.message}
              >
                {(props) => (
                  <Select
                    {...props}
                    {...form.register('depth')}
                    options={[0, 1, 2, 3].map((n) => ({
                      value: String(n),
                      label: n === 0 ? 'Halaman ini saja' : `${n} level ke dalam`,
                    }))}
                  />
                )}
              </Field>

              <Field
                label="Maks. Halaman"
                hint="Batas atas pengaman."
                error={form.formState.errors.maxPages?.message}
              >
                {(props) => (
                  <Input {...props} {...form.register('maxPages')} type="number" min={1} />
                )}
              </Field>

              <Field
                label="Jeda (ms)"
                hint="Jeda antar permintaan."
                error={form.formState.errors.delayMs?.message}
              >
                {(props) => (
                  <Input {...props} {...form.register('delayMs')} type="number" min={200} step={100} />
                )}
              </Field>
            </div>

            {/* Kepatuhan robots.txt: keputusan pengguna, dibatasi paket.
                Yang tidak berhak melihat sakelarnya mati beserta alasannya,
                bukan sakelarnya disembunyikan: menyembunyikan membuat orang
                mengira fiturnya tidak ada, sementara mematikan dengan
                keterangan memberi tahu apa yang perlu dilakukan. */}
            <div className="rounded-lg border border-[var(--border-subtle)] p-4">
              <Toggle
                checked={bolehAturRobots ? form.watch('respectRobotsTxt') : true}
                onChange={(v) => form.setValue('respectRobotsTxt', v)}
                disabled={!bolehAturRobots}
                label="Hormati robots.txt situs sumber"
                description={
                  bolehAturRobots
                    ? 'Sebagian situs melarang seluruh crawler lewat robots.txt, termasuk untuk data yang pemiliknya sendiri persilakan diambil. Matikan hanya kalau Anda memang berhak mengambil data itu.'
                    : `Paket ${namaPaket} wajib menghormati robots.txt. Naik ke Growth atau lebih tinggi untuk bisa mengaturnya sendiri.`
                }
              />
              {bolehAturRobots && !form.watch('respectRobotsTxt') && (
                <p className="mt-3 flex items-start gap-2 rounded-lg bg-warning-50 p-3 text-xs leading-relaxed text-warning-700 dark:bg-warning-500/10 dark:text-warning-500">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                  <span>
                    Anda mematikan penjagaan yang dipasang pemilik situs. Pastikan Anda memang
                    berhak mengambil data ini, dan biarkan jeda antar permintaan tetap wajar supaya
                    situsnya tidak terbebani. Tanggung jawabnya ada pada Anda.
                  </span>
                </p>
              )}
            </div>

            <Field
              label="Selector Item"
              hint="Elemen pembungkus tiap baris data, mis. article.product. Kosongkan bila satu halaman = satu baris."
            >
              {(props) => (
                <Input
                  {...props}
                  {...form.register('itemSelector')}
                  placeholder="article.product_pod"
                  className="font-mono text-[13px]"
                />
              )}
            </Field>

            <Field
              label="Selector Halaman Berikutnya"
              hint="Tautan menuju halaman selanjutnya, mis. li.next a."
            >
              {(props) => (
                <Input
                  {...props}
                  {...form.register('paginationSelector')}
                  placeholder="li.next a"
                  className="font-mono text-[13px]"
                />
              )}
            </Field>

            <div className="border-t border-[var(--border-subtle)] pt-4">
              <Toggle
                checked={values.deduplicate}
                onChange={(v) => form.setValue('deduplicate', v)}
                label="Buang baris duplikat"
                description="Baris dengan isi yang sama persis hanya disimpan sekali."
              />
            </div>
          </CardBody>
        </Card>

        {/* ── Konfigurasi field ───────────────────────────────────────── */}
        <Card>
          <CardHeader
            title="Konfigurasi Field"
            description="Pilih bagian halaman yang datanya ingin Anda kumpulkan."
            action={
              <div className="flex gap-1.5">
                <Button
                  variant="ghost"
                  size="xs"
                  icon={HelpCircle}
                  onClick={() => setHelpOpen(true)}
                >
                  Panduan
                </Button>
                <Button
                  variant="secondary"
                  size="xs"
                  icon={Plus}
                  onClick={() => append({ name: '', selector: '', type: 'text', attribute: '' })}
                >
                  Tambah Field
                </Button>
              </div>
            }
          />

          <CardBody className="space-y-3">
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-sunken)] p-3"
              >
                <div className="flex items-start gap-2">
                  <div className="grid flex-1 gap-2 sm:grid-cols-[1fr_1.4fr_auto]">
                    <Input
                      {...form.register(`fields.${index}.name`)}
                      placeholder="Nama kolom"
                      aria-label={`Nama field ${index + 1}`}
                      size="sm"
                      error={form.formState.errors.fields?.[index]?.name}
                    />
                    <Input
                      {...form.register(`fields.${index}.selector`)}
                      placeholder=".product-title"
                      aria-label={`Selector field ${index + 1}`}
                      size="sm"
                      className="font-mono text-[13px]"
                      error={form.formState.errors.fields?.[index]?.selector}
                    />
                    <Select
                      {...form.register(`fields.${index}.type`)}
                      options={FIELD_TYPES}
                      aria-label={`Tipe field ${index + 1}`}
                      className="sm:w-32"
                    />
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    onClick={() => remove(index)}
                    disabled={fields.length === 1}
                    aria-label={`Hapus field ${index + 1}`}
                    className="mt-0.5 shrink-0 text-[var(--text-muted)] hover:text-danger-600"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>

                {/* Kolom nama atribut hanya relevan untuk tipe "atribut". */}
                {values.fields?.[index]?.type === 'attribute' && (
                  <Input
                    {...form.register(`fields.${index}.attribute`)}
                    placeholder="Nama atribut, mis. data-id"
                    aria-label={`Atribut field ${index + 1}`}
                    size="sm"
                    className="mt-2 font-mono text-[13px]"
                  />
                )}

                {form.formState.errors.fields?.[index] && (
                  <p role="alert" className="mt-2 text-xs font-medium text-danger-600">
                    {form.formState.errors.fields[index]?.name?.message ??
                      form.formState.errors.fields[index]?.selector?.message}
                  </p>
                )}
              </div>
            ))}

            {form.formState.errors.fields?.message && (
              <p role="alert" className="text-sm font-medium text-danger-600">
                {form.formState.errors.fields.message}
              </p>
            )}
          </CardBody>
        </Card>
      </div>

      {/* ── Panel ringkasan ─────────────────────────────────────────────── */}
      <div className="lg:col-span-1">
        <div className="lg:sticky lg:top-20 space-y-4">
          <Card>
            <CardHeader title="Ringkasan" />
            <CardBody className="space-y-3">
              <SummaryRow
                label="Kedalaman"
                value={
                  Number(values.depth) === 0 ? 'Halaman ini saja' : `${values.depth} level`
                }
              />
              <SummaryRow label="Maks. halaman" value={`${values.maxPages} halaman`} />
              <SummaryRow
                label="Jadwal"
                value={
                  values.scheduleEnabled && values.scheduleType !== 'once'
                    ? `${SCHEDULE_LABELS[values.scheduleType]} · ${values.timeOfDay}`
                    : 'Sekali jalan'
                }
              />
              <SummaryRow label="Output" value={OUTPUT_LABELS[values.outputFormat]} />
              <SummaryRow
                label="Field"
                value={`${values.fields?.filter((f) => f.name && f.selector).length ?? 0} field`}
              />
              <SummaryRow
                label="Jeda"
                value={`${(values.delayMs / 1000).toFixed(1).replace('.', ',')} detik`}
              />
            </CardBody>

            <div className="space-y-2 border-t border-[var(--border-subtle)] p-4">
              <Button
                className="w-full"
                icon={Play}
                onClick={submit(true)}
                loading={mutation.isPending && mutation.variables?.runNow}
              >
                Mulai Scraping
              </Button>
              <Button
                variant="secondary"
                className="w-full"
                icon={Save}
                onClick={submit(false)}
                loading={mutation.isPending && !mutation.variables?.runNow}
              >
                Simpan sebagai Draf
              </Button>
            </div>
          </Card>

          <Card>
            <CardHeader title="Penjadwalan" />
            <CardBody className="space-y-4">
              <Field label="Frekuensi">
                {(props) => (
                  <Select
                    {...props}
                    {...form.register('scheduleType')}
                    options={[
                      { value: 'once', label: 'Sekali jalan' },
                      { value: 'hourly', label: 'Setiap jam' },
                      { value: 'daily', label: 'Harian' },
                      { value: 'weekly', label: 'Mingguan' },
                      { value: 'monthly', label: 'Bulanan' },
                    ]}
                  />
                )}
              </Field>

              {values.scheduleType !== 'once' && (
                <>
                  <Field label="Jam eksekusi" error={form.formState.errors.timeOfDay?.message}>
                    {(props) => (
                      <Input {...props} {...form.register('timeOfDay')} type="time" />
                    )}
                  </Field>

                  <Toggle
                    checked={values.scheduleEnabled}
                    onChange={(v) => form.setValue('scheduleEnabled', v)}
                    label="Aktifkan jadwal"
                    description="Job berjalan otomatis sesuai frekuensi di atas."
                  />
                </>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      <SelectorHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
    </form>
  );
};



const SummaryRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 text-sm">
    <dt className="text-[var(--text-muted)]">{label}</dt>
    <dd className="truncate font-semibold text-[var(--text-primary)]">{value}</dd>
  </div>
);

/**
 * Panduan CSS selector.
 *
 * Rancangan awal meminta pengguna mengetik `.product-title` tanpa penjelasan
 * apa pun. Bagi pengguna non-teknis - yang justru menjadi sasaran produk ini - 
 * itu tembok. Panduan singkat ini menurunkannya.
 */
const SelectorHelp = ({ open, onClose }) => (
  <Modal
    open={open}
    onClose={onClose}
    title="Cara menemukan selector"
    description="Selektor adalah alamat sebuah bagian di dalam halaman. Ia yang memberi tahu Crawel isi mana yang harus diambil."
    size="lg"
  >
    <div className="space-y-5 text-sm leading-relaxed text-[var(--text-secondary)]">
      <section>
        <h3 className="font-bold text-[var(--text-primary)]">Langkah cepat</h3>
        <ol className="mt-2 list-decimal space-y-1.5 pl-5">
          <li>Buka halaman target di Chrome.</li>
          <li>Klik kanan pada data yang ingin diambil, pilih <strong>Inspect</strong>.</li>
          <li>Klik kanan pada baris HTML yang tersorot, pilih <strong>Copy → Copy selector</strong>.</li>
          <li>Tempel di kolom selector.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-[var(--text-primary)]">Pola yang sering dipakai</h3>
        <div className="mt-2 overflow-hidden rounded-lg border border-[var(--border-subtle)]">
          <table className="w-full text-[13px]">
            <tbody className="divide-y divide-[var(--border-subtle)]">
              {[
                ['.harga', 'Elemen dengan class "harga"'],
                ['#judul', 'Elemen dengan id "judul"'],
                ['h2 a', 'Tautan di dalam heading h2'],
                ['.produk .nama', '"nama" di dalam "produk"'],
                ['img', 'Semua gambar'],
              ].map(([selector, meaning]) => (
                <tr key={selector}>
                  <td className="w-40 bg-[var(--surface-sunken)] px-3 py-2 font-mono text-[12px] text-[var(--text-primary)]">
                    {selector}
                  </td>
                  <td className="px-3 py-2 text-[var(--text-muted)]">{meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section
        className={cn(
          'rounded-lg border border-info-200 bg-info-50 p-3.5',
          'dark:border-info-500/25 dark:bg-info-500/10',
        )}
      >
        <h3 className="font-bold text-info-700 dark:text-info-500">Tips</h3>
        <p className="mt-1 text-info-700/90 dark:text-info-500/90">
          Isi <strong>Selector Item</strong> bila halaman berisi daftar (katalog, hasil pencarian).
          Selector field lalu dicari <em>di dalam</em> setiap item, sehingga satu halaman
          menghasilkan banyak baris rapi alih-alih satu baris berantakan.
        </p>
      </section>
    </div>
  </Modal>
);

/**
 * Empat cara membuat scraping, dari yang paling dituntun sampai paling bebas:
 *
 *   Otomatis : tempel URL, deteksi mengisi semuanya. Untuk sebagian besar kasus.
 *   AI       : sama, plus penamaan kolom oleh model Claude memakai kunci user.
 *   Pustaka  : formulir siap pakai buatan dev; user tinggal mengisi parameter.
 *   Manual   : kendali penuh atas selektor dan cakupan crawl.
 *
 * Otomatis sengaja menjadi tab pertama: mode paling sederhana harus menjadi
 * yang pertama terlihat, dan Manual tetap satu klik saja jauhnya.
 */
const MODE = [
  ['otomatis', 'Otomatis'],
  ['ai', 'AI'],
  ['pustaka', 'Pustaka'],
  ['manual', 'Manual'],
];

export const JobCreatePage = () => {
  const [mode, setMode] = useState('otomatis');

  return (
    <div className="space-y-4">
      <div
        role="tablist"
        aria-label="Mode pembuatan scraping"
        className="inline-flex gap-1 rounded-xl bg-[var(--surface-sunken)] p-1"
      >
        {MODE.map(([nilai, label]) => (
          <button
            key={nilai}
            role="tab"
            type="button"
            aria-selected={mode === nilai}
            onClick={() => setMode(nilai)}
            className={
              mode === nilai
                ? 'rounded-lg bg-[var(--surface-raised)] px-4 py-2 text-sm font-semibold text-[var(--text-primary)] shadow-xs'
                : 'rounded-lg px-4 py-2 text-sm font-medium text-[var(--text-muted)] transition-colors hover:text-[var(--text-secondary)]'
            }
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'otomatis' && <SimpleMode />}
      {mode === 'ai' && <SimpleMode ai key="ai" />}
      {mode === 'pustaka' && <TemplateMode />}
      {mode === 'manual' && <ManualMode />}
    </div>
  );
};
